import React from 'react';
import { Avatar, Box, Container, Grid, Stack, Typography } from '@mui/material';
import { ChevronRight, Mail, Phone, MessageCircle, Headphones } from 'lucide-react';
import MainCard from 'ui-component/cards/MainCard';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { getContactInfo } from 'store/authAction';

const getContactTheme = (title) => {
    const value = title?.toLowerCase();

    // Apple-inspired system colors
    if (value.includes('whatsapp')) {
        return { icon: MessageCircle, color: '#34C759', bg: '#E8F8EE' }; // System Green
    }
    if (value.includes('email')) {
        return { icon: Mail, color: '#007AFF', bg: '#E5F1FF' }; // System Blue
    }
    if (value.includes('mobile') || value.includes('phone')) {
        return { icon: Phone, color: '#5856D6', bg: '#EFEFFB' }; // System Indigo
    }

    return { icon: Headphones, color: '#8E8E93', bg: '#F2F2F7' }; // System Gray
};

const Support = () => {
    const { contacts } = useSelector((state) => state.auth);
    const dispatch = useDispatch();

    const openLink = (link) => {
        if (!link) return;

        if (link.startsWith('whatsapp://')) {
            const phone = link.split('phone=')[1];
            window.open(`https://wa.me/${phone}`, '_blank');
            return;
        }

        window.open(link, '_blank');
    };

    useEffect(() => {
        dispatch(getContactInfo());
    }, [dispatch]);

    return (
        <MainCard content={false} sx={{ border: 'none', bgcolor: '#FFFFFF', borderRadius: '24px' }}>
            <Container
                maxWidth="md"
                sx={{
                    py: { xs: 6, sm: 8, md: 10 },
                    px: { xs: 3, sm: 4 }
                }}
            >
                {/* HERO SECTION */}
                <Stack spacing={2} alignItems="center" textAlign="center" mb={8}>
                    <Avatar
                        sx={{
                            width: 72,
                            height: 72,
                            bgcolor: '#F5F5F7',
                            color: '#1D1D1F',
                            mb: 2
                        }}
                    >
                        <Headphones size={32} />
                    </Avatar>

                    <Typography
                        variant="h1"
                        sx={{
                            fontWeight: 700,
                            color: '#1D1D1F',
                            letterSpacing: '-0.02em',
                            fontSize: { xs: '2.25rem', sm: '3rem', md: '3.5rem' },
                            lineHeight: 1.1
                        }}
                    >
                        How can we help?
                    </Typography>

                    <Typography
                        sx={{
                            color: '#86868B',
                            fontSize: { xs: '1.05rem', md: '1.2rem' },
                            maxWidth: 520,
                            lineHeight: 1.5,
                            mt: 1
                        }}
                    >
                        Choose a preferred support channel below to connect with our customer service team instantly.
                    </Typography>
                </Stack>

                {/* CONTACT CHANNELS */}
                <Grid container spacing={2}>
                    {contacts?.map((item) => {
                        const theme = getContactTheme(item.title);
                        const Icon = theme.icon;

                        return (
                            <Grid item xs={12} sm={6} key={item.id}>
                                <Box
                                    onClick={() => openLink(item.link)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        p: 2.5,
                                        borderRadius: '20px',
                                        bgcolor: '#F5F5F7',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            bgcolor: '#E8E8ED',
                                            transform: 'scale(0.98)'
                                        },
                                        '&:active': {
                                            transform: 'scale(0.95)'
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 52,
                                            height: 52,
                                            borderRadius: '14px',
                                            bgcolor: theme.bg,
                                            color: theme.color,
                                            mr: 2.5
                                        }}
                                    >
                                        <Icon size={26} strokeWidth={2.5} />
                                    </Box>

                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                fontWeight: 600,
                                                color: '#1D1D1F',
                                                fontSize: '1.05rem',
                                                mb: 0.2
                                            }}
                                        >
                                            {item.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#86868B',
                                                fontSize: '0.9rem',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}
                                        >
                                            {item.contact}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ color: '#C7C7CC', display: 'flex', alignItems: 'center' }}>
                                        <ChevronRight size={20} strokeWidth={2.5} />
                                    </Box>
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>
            </Container>
        </MainCard>
    );
};

export default Support;
